import React, { useEffect, useState } from "react";

import { FaUsers, FaBolt } from "react-icons/fa";
import { MdElevator } from "react-icons/md";
import { AiOutlineAlert } from "react-icons/ai";
import { API_BASE } from '../config'

function StatusCard({ title, value, icon, status = "good", onClick }) {
  const statusColors = {
    good: "bg-green-50 border-green-200",
    warning: "bg-yellow-50 border-yellow-200",
    error: "bg-red-50 border-red-200",
  };

  return (
    <div
      className={`p-4 rounded-lg border-2 ${statusColors[status]} ${onClick ? 'cursor-pointer active:scale-95 transition-transform' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-sm">{title}</p>
          <p className="mt-2">{value}</p>
        </div>
        <div className="text-gray-700">{icon}</div>
      </div>
      {onClick && <p className="text-xs text-blue-500 mt-2">Tap to view →</p>}
    </div>
  );
}

export default function Dashboard({ stats, onNavigate }) {
  const [dashboardData, setDashboardData] = useState({
    activeVisitors: 0,
    pendingApprovals: 0,
    totalDevices: 0,
    operationalDevices: 0,
  });

  const [userCommunity, setUserCommunity] = useState(null);

  // decode id_token to extract email
  function decodeIdTokenEmail() {
    try {
      const t = sessionStorage.getItem('id_token')
      if (!t) return null
      const payload = t.split('.')[1]
      const json = decodeURIComponent(atob(payload.replace(/-/g, '+').replace(/_/g, '/')).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))
      const p = JSON.parse(json)
      return (p.email || '').toString().toLowerCase()
    } catch (e) {
      console.warn('failed to decode id_token', e)
      return null
    }
  }

  async function fetchUserRecord(email) {
    try {
      const token = sessionStorage.getItem('id_token')
      const url = `${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!resp.ok) return null
      const data = await resp.json()
      // Multi-community: pick the community the user selected (stored in sessionStorage)
      if (data.multiCommunity && Array.isArray(data.communities)) {
        const selectedCid = sessionStorage.getItem('community_id') || ''
        const selectedUid = sessionStorage.getItem('user_id') || ''
        return data.communities.find(c => {
          const cid = c.COMMUNITY_ID || c.communityId || ''
          const uid = c.USER_ID || c.userId || ''
          return selectedUid ? (cid === selectedCid && uid === selectedUid) : (cid === selectedCid)
        }) || data.communities[0] || null
      }
      return data
    } catch (e) {
      console.error('fetchUserRecord error', e)
      return null
    }
  }

  async function loadDashboardData() {
    try {
      const email = decodeIdTokenEmail()
      let userRecord = null
      if (email) {
        userRecord = await fetchUserRecord(email)
        if (userRecord) {
          const communityId = userRecord.COMMUNITY_ID || userRecord.communityId || userRecord.COMM_VISTRS_PRTN_KEY_ID
          setUserCommunity(communityId)

          const token = sessionStorage.getItem('id_token')

          // Fetch visitors
          let activeVisitors = 0
          let pendingApprovals = 0
          try {
            const visitorsUrl = `${API_BASE}/visitors?communityId=${encodeURIComponent(communityId)}`
            const visitorsResp = await fetch(visitorsUrl, { headers: { Authorization: `Bearer ${token}` } })
            if (visitorsResp.ok) {
              const visitors = await visitorsResp.json()
              const visitorsList = Array.isArray(visitors) ? visitors : []
              // Normalize status: the API may return STATUS or status, default to 'pending' when absent
              const getStatus = (v) => (v.STATUS || v.status || 'pending').toString().toLowerCase()
              activeVisitors = visitorsList.filter(v => {
                const s = getStatus(v)
                return s === 'checked-in'
              }).length
              pendingApprovals = visitorsList.filter(v => getStatus(v) === 'pending').length
            }
          } catch (e) {
            console.error('Error fetching visitors:', e)
          }

          // Fetch devices
          let totalDevices = 0
          let operationalDevices = 0
          try {
            const devicesUrl = `${API_BASE}/devices?communityId=${encodeURIComponent(communityId)}`
            const devicesResp = await fetch(devicesUrl, { headers: { Authorization: `Bearer ${token}` } })
            if (devicesResp.ok) {
              const devices = await devicesResp.json()
              const devicesList = Array.isArray(devices) ? devices : []
              totalDevices = devicesList.length
              operationalDevices = devicesList.filter(d => d.STATUS?.toLowerCase() === 'operational' || d.status?.toLowerCase() === 'operational').length
            }
          } catch (e) {
            console.error('Error fetching devices:', e)
          }

          setDashboardData({
            activeVisitors,
            pendingApprovals,
            totalDevices,
            operationalDevices,
          })
        }
      }
    } catch (e) {
      console.error('Error loading dashboard data:', e)
    }
  }

  useEffect(() => {
    loadDashboardData()
  }, [])

  const getLiftStatusText = () => {
    if (dashboardData.totalDevices === 0) {
      return "No Devices"
    }
    if (dashboardData.operationalDevices === dashboardData.totalDevices) {
      return "All Devices Operational"
    }
    return `${dashboardData.operationalDevices}/${dashboardData.totalDevices} Operational`
  };

  const getLiftStatus = () => {
    if (dashboardData.totalDevices === 0) {
      return "good"
    }
    if (dashboardData.operationalDevices === dashboardData.totalDevices) {
      return "good"
    }
    if (dashboardData.operationalDevices === 0) {
      return "error"
    }
    return "warning"
  };

  return (
    <div className="p-4">
      <div className="mb-6">
        <h2 className="text-lg font-bold">Property Dashboard</h2>
        <p className="text-sm">Overview of community status</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatusCard
          title="Active Visitors"
          value={dashboardData.activeVisitors}
          icon={<FaUsers size={24} />}
          status="good"
          onClick={() => onNavigate?.('visitors', 'checked-in')}
        />

        <StatusCard
          title="Pending Approvals"
          value={dashboardData.pendingApprovals}
          icon={<AiOutlineAlert size={24} />}
          status={dashboardData.pendingApprovals > 0 ? "warning" : "good"}
          onClick={() => onNavigate?.('visitors', 'pending')}
        />

        <StatusCard
          title="Device Status"
          value={getLiftStatusText()}
          icon={<MdElevator size={24} />}
          status={getLiftStatus()}
          onClick={() => onNavigate?.('devices')}
        />

        <StatusCard
          title="Total Devices"
          value={dashboardData.totalDevices}
          icon={<FaBolt size={24} />}
          status={dashboardData.operationalDevices === dashboardData.totalDevices ? "good" : "warning"}
          onClick={() => onNavigate?.('devices')}
        />
      </div>
    </div>
  );
}
